/**
 * StepIndicator Component
 *
 * Progress bar with step circles for the onboarding wizard.
 * Shows current step, completed steps, and remaining steps.
 *
 * Design Notes:
 * - Uses brand purple for active/completed states
 * - Animated transitions on step changes
 * - Step circles with icons indicate progress
 */

import { BlockStack, InlineStack, Text, ProgressBar, Icon } from '@shopify/polaris';
import { CheckIcon } from '@shopify/polaris-icons';
import { BRAND_PURPLE, BRAND_PURPLE_SHADOW } from '../../constants/brand';
import type { Step, StepId } from './types';

export interface StepIndicatorProps {
  /** Current active step ID */
  currentStep: StepId;
  /** Array of step configurations */
  steps: Step[];
}

export function StepIndicator({ currentStep, steps }: StepIndicatorProps) {
  // Filter to only steps that should show in progress bar
  const progressSteps = steps.filter((s) => s.showInProgress);
  const currentIndex = progressSteps.findIndex((s) => s.id === currentStep);
  const progress = currentIndex >= 0 ? ((currentIndex + 1) / progressSteps.length) * 100 : 100;

  return (
    <BlockStack gap="400">
      {/* Progress Bar - uses wizard-progress-bar class for brand color */}
      <div className="wizard-progress-bar" style={{ position: 'relative', width: '100%' }}>
        <ProgressBar progress={progress} size="small" tone="primary" />
      </div>

      {/* Step Circles */}
      <InlineStack gap="0" align="space-between">
        {progressSteps.map((step, index) => {
          const isActive = index === currentIndex;
          const isCompleted = index < currentIndex;
          const StepIcon = step.icon;
          // Stagger class for entrance animation (1-indexed)
          const entranceClass = `wizard-stagger-item wizard-entrance-step-${index + 1}`;

          return (
            <div key={step.id} style={{ flex: '1 1 0', minWidth: 0 }} className={entranceClass}>
              <BlockStack gap="200" inlineAlign="center">
                {/* Circle with icon - enhanced visibility */}
                <div
                  className={`wizard-step-circle ${isActive ? 'wizard-step-circle--active' : ''} ${isCompleted ? 'wizard-step-circle--completed' : ''}`}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isActive || isCompleted ? BRAND_PURPLE : 'white',
                    border: isActive || isCompleted ? 'none' : '2px solid #d1d5db',
                    boxShadow: isActive
                      ? `0 0 0 4px ${BRAND_PURPLE_SHADOW}, 0 2px 8px rgba(139, 123, 216, 0.3)`
                      : isCompleted
                        ? '0 2px 4px rgba(139, 123, 216, 0.15)'
                        : '0 1px 3px rgba(0, 0, 0, 0.08)',
                    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon
                      source={isCompleted ? CheckIcon : StepIcon}
                      tone={isActive || isCompleted ? 'inherit' : 'subdued'}
                    />
                  </div>
                </div>

                {/* Step label */}
                <Text
                  as="p"
                  variant="bodySm"
                  alignment="center"
                  tone={isActive ? 'base' : 'subdued'}
                  fontWeight={isActive ? 'semibold' : 'regular'}
                >
                  {step.title}
                </Text>
              </BlockStack>
            </div>
          );
        })}
      </InlineStack>
    </BlockStack>
  );
}
